import React from 'react';
import { Column } from 'react-table';
import { useQuery } from '@tanstack/react-query';
import { ColumnUtilType, Option, SearchOption, TableListData } from '../../../types/common/table';
import { Link } from '../../../types/common/link';
import LinkItem from '../LinkItem';

interface LinkIdProps<T extends { id: number }, F extends { id?: number }> {
  queryKey: string[];
  value?: Link;
  columns: (utils: ColumnUtilType<T, F>) => Column<T>[];
  fetcher: (option: SearchOption<F>) => Promise<TableListData<T>>;
  onChange?: (selected: Link) => void;
  initialOption?: Option<F>;
  formatter?: (value: T) => Link;
  trigger: (openModal: () => void) => JSX.Element;
}

const LinkId = <T extends { id: number }, F extends { id?: number }>(props: LinkIdProps<T, F>) => {
  const { formatter, onChange, value, queryKey, columns, fetcher, initialOption, trigger } = props;

  const { data: selectedItem } = useQuery<TableListData<T>, unknown, T>(
    [...queryKey, value?.id],
    () => fetcher({ id: value!.id } as SearchOption<F>),
    {
      enabled: !!value?.id,
      select: (res) => res.list[0],
    },
  );

  const handleChange = (value: T & { id?: number; name?: string }) => {
    let link: Link | undefined;
    link = formatter && formatter(value);
    if (value.id && value.name) {
      link = { id: value.id, name: value.name };
    }

    if (link) {
      onChange && onChange(link);
    }
  };

  return (
    <LinkItem
      queryKey={queryKey}
      value={selectedItem}
      fetcher={fetcher}
      onChange={handleChange}
      columns={columns}
      initialOption={initialOption}
      trigger={trigger}
    />
  );
};

export default LinkId;
